import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { ArrowLeft, Share2, Shield, MapPin, Phone, Globe, Mail, GraduationCap, Building2, Clock, Video, Heart, ChevronRight, ShieldCheck, Check, Star, Calendar, CheckCircle2, Circle, HelpCircle, PlusCircle, DollarSign } from 'lucide-react'
import { doctorsAPI, favoritesAPI } from '../../services/api'
import { useProfileStore } from '../../stores'
import { isDoctorInNetwork, getInsuranceMatchType, patientHasInsurance as hasInsuranceFn } from '../../utils/insuranceMatch'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import StartCaseSheet from './StartCaseSheet'
import { useI18n } from '../../i18n'
import { Share as CapShare } from '@capacitor/share'
import { Capacitor } from '@capacitor/core'
import { formatAddress } from '../../utils/formatAddress'
import { formatSex } from '../../utils/formatSex'
import NetworkDisclaimer from '../../components/NetworkDisclaimer' // s105 (TestFlight b71)
import { clinicianName } from '../../utils/clinicianName.js'
import { safePath } from '../../utils/safePath'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// v230.5.0: Plan-specific in-network / eligibility row for the MD profile.
// Surfaces the existing insuranceMatch tri-state (confirmed / no_match / unknown)
// plus a "no insurance on file" state, as colored TEXT (no filled pills/boxes) —
// green = in-network, navy = neutral, blue = action. Active-coverage sub-line is
// pulled from the patient's last Stedi eligibility (resultClass) when present.
const COV_GREEN = '#15803D'
const COV_NAVY = '#15354F'
const COV_BLUE = '#46A8F9'
const COV_SLATE = '#64748B'

function _covRelDay(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const days = Math.floor((Date.now() - d.getTime()) / 86400000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// v235.7.1 — Self-pay coverage block. Replaces the insurance/eligibility prompt
// for patients who chose self-pay. Shows the doctor's whole-dollar cash rates,
// or a welcome / insurance-required advisory.
function SelfPayStatus({ doctor, navigate }) {
  const { t } = useI18n()
  const acceptsSelfPay = doctor?.accept_self_pay !== false
  const io = doctor?.self_pay_in_office_rate
  const vr = doctor?.self_pay_virtual_rate
  const hasIO = io != null && !isNaN(Number(io))
  const hasVR = vr != null && !isNaN(Number(vr))

  const wrap = 'flex items-start gap-2.5 mt-3.5 pt-3.5 pb-3.5 text-left'
  const wrapStyle = { borderTop: '1px solid #E7EEF4', borderBottom: '1px solid #E7EEF4' }

  // Accepts self-pay AND at least one listed rate → cash-price card
  if (acceptsSelfPay && (hasIO || hasVR)) {
    return (
      <div className="mt-3.5 pt-3.5 pb-1" style={{ borderTop: '1px solid #E7EEF4' }}>
        <div className="rounded-2xl p-3.5" style={{ background: '#E9F8FF', border: '1px solid #A7D9FC' }}>
          <div className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide" style={{ color: COV_NAVY }}>
            <DollarSign size={13} /> {t('doctorProfile.selfPayPrice', 'Self-pay price')}
          </div>
          <div className="flex gap-2.5 mt-2.5">
            {hasIO && (
              <div className="flex-1 rounded-xl p-2.5" style={{ background: '#fff', border: '1px solid #DCEBF6' }}>
                <div className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: COV_SLATE }}>
                  <Building2 size={12} /> {t('doctorProfile.inOffice')}
                </div>
                <div className="text-[19px] font-extrabold mt-1" style={{ color: COV_NAVY, letterSpacing: '-.01em' }}>${Number(io)}</div>
                <div className="text-[10.5px] font-semibold" style={{ color: COV_SLATE }}>per visit</div>
              </div>
            )}
            {hasVR && (
              <div className="flex-1 rounded-xl p-2.5" style={{ background: '#fff', border: '1px solid #DCEBF6' }}>
                <div className="flex items-center gap-1.5 text-[11px] font-bold" style={{ color: COV_SLATE }}>
                  <Video size={12} /> {t('caseHome.virtual', 'Virtual')}
                </div>
                <div className="text-[19px] font-extrabold mt-1" style={{ color: COV_NAVY, letterSpacing: '-.01em' }}>${Number(vr)}</div>
                <div className="text-[10.5px] font-semibold" style={{ color: COV_SLATE }}>per visit</div>
              </div>
            )}
          </div>
          <p className="text-[11px] leading-snug mt-2.5" style={{ color: '#41566A' }}>
            You're booking as self-pay — this is what you'll be charged. No insurance needed.
          </p>
        </div>
      </div>
    )
  }

  // Accepts self-pay but no rate listed → welcome advisory
  if (acceptsSelfPay) {
    return (
      <div className={wrap} style={wrapStyle}>
        <DollarSign size={17} style={{ color: COV_GREEN, marginTop: 1, flexShrink: 0 }} />
        <div className="min-w-0">
          <p className="text-[13.5px] font-extrabold leading-tight" style={{ color: COV_GREEN }}>{t('doctorProfile.selfPayWelcome', 'Self-pay welcome')}</p>
          <p className="text-[11.5px] font-medium leading-snug mt-0.5" style={{ color: COV_SLATE }}>
            {t('doctorProfile.selfPayWelcomeBody', 'This office accepts self-pay patients. Contact them for pricing before booking.')}
          </p>
        </div>
      </div>
    )
  }

  // Does not accept self-pay → insurance required
  return (
    <div className={wrap} style={wrapStyle}>
      <DollarSign size={17} style={{ color: COV_SLATE, marginTop: 1, flexShrink: 0 }} />
      <div className="min-w-0">
        <p className="text-[13.5px] font-extrabold leading-tight" style={{ color: COV_NAVY }}>{t('doctorProfile.insuranceRequired', 'Insurance required')}</p>
        <p className="text-[11.5px] font-medium leading-snug mt-0.5" style={{ color: COV_SLATE }}>
          {t('doctorProfile.insuranceRequiredBody', "This office doesn't take self-pay patients.")}
        </p>
        <button onClick={() => navigate('/doctors')} className="text-[12px] font-extrabold mt-1.5" style={{ color: COV_BLUE }}>
          See self-pay doctors →
        </button>
      </div>
    </div>
  )
}

function CoverageStatus({ doctor, insurance, navigate }) {
  // v235.7.1 — Self-pay patients don't need a network/eligibility prompt; show the
  // doctor's cash price (or a welcome/insurance-required advisory) instead.
  if (insurance?.selfPay) {
    return <SelfPayStatus doctor={doctor} navigate={navigate} />
  }
  // State: 'none' (no insurance on file) | 'confirmed' | 'no_match' | 'unknown'
  const state = !hasInsuranceFn(insurance) ? 'none' : getInsuranceMatchType(doctor, insurance)
  const planLabel = insurance?.planName || insurance?.provider || 'your plan'
  const elig = insurance?.last_eligibility
  const rc = (elig?.resultClass || elig?.status || '').toLowerCase()
  const coverageActive = rc === 'active' || rc === 'verified'
  const checkedRel = _covRelDay(elig?.checkedAt)
  const officePhone = doctor?.practice_phone || doctor?.phone || ''

  let Icon, iconColor, title, titleColor, sub, action
  if (state === 'confirmed') {
    Icon = CheckCircle2; iconColor = COV_GREEN; titleColor = COV_GREEN
    title = `In-network with ${planLabel}`
    sub = coverageActive
      ? `Active coverage${checkedRel ? ` · checked ${checkedRel}` : ''}`
      : `In your plan's network`
  } else if (state === 'no_match') {
    Icon = Circle; iconColor = COV_SLATE; titleColor = COV_NAVY
    title = `Out-of-network for ${planLabel}`
    sub = coverageActive
      ? `Your coverage is active, but this office isn't in your plan's network — a visit may cost more.`
      : `This office isn't in your plan's network — a visit may cost more.`
    action = { label: 'See in-network doctors →', onClick: () => navigate('/doctors') }
  } else if (state === 'unknown') {
    Icon = HelpCircle; iconColor = COV_SLATE; titleColor = COV_NAVY
    title = `Network status unverified`
    sub = `This office hasn't listed your plan. Confirm with them before booking.`
    if (officePhone) action = { label: `Call office · ${officePhone}`, href: `tel:${String(officePhone).replace(/[^\d+]/g, '')}` }
  } else {
    Icon = PlusCircle; iconColor = COV_BLUE; titleColor = COV_BLUE
    title = `Add your insurance to check coverage`
    sub = `We'll tell you instantly whether this doctor is in your plan's network.`
    action = { label: 'Add insurance →', onClick: () => navigate('/profile/insurance') }
  }

  return (
    <div className="flex items-start gap-2.5 mt-3.5 pt-3.5 pb-3.5 text-left" style={{ borderTop: '1px solid #E7EEF4', borderBottom: '1px solid #E7EEF4' }}>
      <Icon size={17} style={{ color: iconColor, marginTop: 1, flexShrink: 0 }} />
      <div className="min-w-0">
        <p className="text-[13.5px] font-extrabold leading-tight" style={{ color: titleColor }}>{title}</p>
        <p className="text-[11.5px] font-medium leading-snug mt-0.5" style={{ color: COV_SLATE }}>{sub}</p>
        {state === 'confirmed' && <NetworkDisclaimer className="mt-1.5" />}
        {action && (action.href ? (
          <a href={action.href} className="inline-block text-[12px] font-extrabold mt-1.5" style={{ color: COV_BLUE }}>{action.label}</a>
        ) : (
          <button onClick={action.onClick} className="text-[12px] font-extrabold mt-1.5" style={{ color: COV_BLUE }}>{action.label}</button>
        ))}
      </div>
    </div>
  )
}

export default function DoctorProfileScreen({
  // v214.9.3: When these props are supplied, the component is being rendered
  // as a bottom-sheet body (typically from ShareScreen). Otherwise it falls
  // back to react-router useParams/useLocation/useNavigate for the standalone
  // route at /doctor/:doctorId.
  doctorIdProp,
  modeProp,
  isSelectedProp,
  // TEST 3.15 (patient push 3a): { label, onPress } — the send conversation's pending step,
  // offered when this physician is the one already chosen (ShareScreen's confirm step)
  selectedCtaProp,
  onCloseProp,
  onPrimaryActionProp,
  asSheet = false,
} = {}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const params = useParams()
  const location = useLocation()

  const doctorId = doctorIdProp || params.doctorId
  const mode = modeProp || location.state?.mode || 'browse'
  const isSelected = isSelectedProp !== undefined
    ? isSelectedProp
    : (location.state?.isSelected || false)
  // PROD-1362: history state is not trusted as a destination; a non in-app path falls back to
  // null, which keeps the navigate(-1) behaviour below.
  const returnTo = safePath(location.state?.returnTo, null)

  // v214.9.3: in sheet mode, close = call onCloseProp. In route mode, close = navigate(-1).
  const goBack = () => {
    if (onCloseProp) onCloseProp()
    else navigate(-1)
  }
  
  const [doctor, setDoctor] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAllInsurance, setShowAllInsurance] = useState(false)
  const [isFavorite, setIsFavorite] = useState(false)
  const [favLoading, setFavLoading] = useState(false)
  const [nextSlot, setNextSlot] = useState(null) // { date, time } or null
  const [showStartCase, setShowStartCase] = useState(false)

  // INS-1: Must be called before any early returns (React hooks rule)
  const patientInsurance = useProfileStore((s) => s.profile?.insurance)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [data, favs] = await Promise.all([
          doctorsAPI.getFullProfile(doctorId),
          favoritesAPI.getDoctors()
        ])
        if (data) setDoctor(data)
        else if (location.state?.doctor) setDoctor(location.state.doctor)
        setIsFavorite(favs.includes(doctorId))
        // Fetch next available slot (non-blocking)
        doctorsAPI.getAvailableSlots(doctorId, 14).then(result => {
          if (result?.enabled && result?.slots?.length > 0) {
            const first = result.slots[0]
            setNextSlot(first)
          }
        }).catch(() => {})
      } catch (err) {
        console.error('[DoctorProfile] Failed to load:', err)
        if (location.state?.doctor) setDoctor(location.state.doctor)
      } finally {
        setLoading(false)
      }
    }
    if (doctorId) load()
  }, [doctorId])

  const toggleFavorite = async () => {
    setFavLoading(true)
    try {
      if (isFavorite) {
        await favoritesAPI.removeDoctor(doctorId)
        setIsFavorite(false)
      } else {
        await favoritesAPI.addDoctor(doctorId)
        setIsFavorite(true)
      }
    } catch (err) {
      console.error('[Favorite] Toggle error:', err)
    } finally {
      setFavLoading(false)
    }
  }

  // ★★ P1 round 4 (L2; Alex D-g). "Start a case" and "Select Doctor" lead to a send, and a
  //   send to an Orella doctor requires a time. The profile reads the `bookable` that GET
  //   /doctors/<id>/full-profile now carries, through the share picker's own hasRealHours
  //   (one rule, no second reading here). Without real hours the CTA is replaced by
  //   "Online booking isn't available for this doctor" and the handler refuses. 'view'
  //   shows no CTA and 'booking' only goes back, so neither is a way to a send.
  // ★★ D527 (Dr. Rami 2026-10-23, time request; handoff s116): REVERSED. Without real hours the CTA is the normal
  //   "Start a case" / "Select Doctor"; the send asks the patient to REQUEST a time, which the physician confirms.

  const handlePrimaryCTA = () => {
    // ★★ TEST 3.15 (patient push 3a): the physician is already chosen and the sheet sits on the
    //   send conversation: the CTA is that conversation's pending step and does it. It was
    //   "Continue" (v217.1.2) and only closed the sheet, like the back arrow.
    if (isSelected && selectedCtaProp) {
      selectedCtaProp.onPress()
      return
    }
    // v214.9.3: In sheet mode the parent owns navigation (e.g. ShareScreen
    // wants to set selectedDoctor and advance to confirm-doctor). Hand it the
    // doctor object and close ourselves.
    if (onPrimaryActionProp) {
      onPrimaryActionProp(doctor)
      onCloseProp?.()
      return
    }
    if (mode === 'select') {
      // Navigate back with doctor data so the calling screen can use it
      navigate(returnTo || -1, { 
        state: { selectedDoctor: doctor },
        replace: true,
      })
    } else if (mode === 'booking') {
      navigate(-1)
    } else {
      // v230.6.0: "Start a case" now opens the specialty-scoped picker sheet,
      // which deep-links this MD and routes into the shared case-start flow.
      setShowStartCase(true)
    }
  }

  // CTA label based on mode
  const getCTALabel = () => {
    // TEST 3.15: when the MD is already chosen, the CTA names the pending step (translated)
    if (mode === 'select') return isSelected && selectedCtaProp ? selectedCtaProp.label : 'Select Doctor'
    if (mode === 'booking') return 'Book appointment'
    return 'Start a case'
  }

  const handleShare = async () => {
    // v242.20.0 (FB8) — the old handler only fired when navigator.share existed and
    // swallowed all errors, so on the iOS WKWebView the button did nothing. Use the
    // native Capacitor share sheet on device, navigator.share on the web, and a
    // clipboard copy as the last resort. window.location.href was a local capacitor://
    // URL (not shareable) — share a real public link instead.
    const name = clinicianName(doctor || {})
    const specialty = (doctor?.specialties && doctor.specialties[0]) || ''
    const shareData = {
      title: specialty ? `${name} — ${specialty}` : name,
      text: `Check out ${name} on Orella Health`,
      url: 'https://orellahealth.com',
    }
    try {
      if (Capacitor?.isNativePlatform?.()) {
        await CapShare.share(shareData)
      } else if (navigator.share) {
        await navigator.share(shareData)
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${shareData.text} — ${shareData.url}`)
      }
    } catch { /* user cancelled or share unavailable */ }
  }

  const getDirectionsUrl = () => {
    // v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string
    const addr = [formatAddress(doctor?.practice_address), doctor?.practice_city, doctor?.practice_state, doctor?.practice_zip].filter(Boolean).join(', ')
    return `https://maps.google.com/?q=${encodeURIComponent(addr)}`
  }

  const formatPhone = (phone) => {
    if (!phone) return ''
    const digits = phone.replace(/\D/g, '')
    if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`
    return phone
  }

  if (loading) {
    const body = (
      <div className={`${asSheet ? 'min-h-[40vh]' : 'screen-min-h'} bg-white flex items-center justify-center`}>
        <div className="text-center">
          <OrellaSpinner size={32} className="mx-auto mb-3" />
          <p className="text-sm text-gray-500">{t('doctorProfile.loadingDoctorProfile')}</p>
        </div>
      </div>
    )
    return asSheet ? body : <ResponsiveWrapper className="bg-white shadow-xl" safeArea={false}>{body}</ResponsiveWrapper>
  }

  if (!doctor) {
    const body = (
      <div className={`${asSheet ? 'min-h-[40vh]' : 'screen-min-h'} bg-white flex flex-col items-center justify-center p-8`}>
        <p className="text-gray-600 mb-4">{t('doctorProfile.doctorProfileNotFound')}</p>
        <button onClick={goBack} className="text-orella-500 font-medium">{t('doctorProfile.goBack')}</button>
      </div>
    )
    return asSheet ? body : <ResponsiveWrapper className="bg-white shadow-xl" safeArea={false}>{body}</ResponsiveWrapper>
  }

  const initials = `${doctor.first_name?.[0] || ''}${doctor.last_name?.[0] || ''}`
  const fullName = clinicianName(doctor, 'formal')
  
  // accepted_insurance: API returns flat providers array OR raw JSONB { providers, accept_all, ipas }
  const rawInsurance = doctor.accepted_insurance
  const acceptAll = doctor.accept_all_insurance || rawInsurance?.accept_all || false
  // If API already flattened to array, use directly; otherwise extract from JSONB
  const insuranceProviders = Array.isArray(rawInsurance) 
    ? rawInsurance 
    : (rawInsurance?.providers || [])
  // Normalize to objects with plan types
  const insuranceItems = insuranceProviders.map(ins => {
    if (typeof ins === 'string') return { name: ins, acceptedPlanTypes: [] }
    return { name: ins?.name || ins?.id || '', acceptedPlanTypes: ins?.acceptedPlanTypes || [] }
  }).filter(i => i.name)
  const visibleInsurance = showAllInsurance ? insuranceItems : insuranceItems.slice(0, 4)
  // IPAs — API returns as top-level accepted_ipas, or nested in JSONB
  const acceptedIPAs = doctor.accepted_ipas || rawInsurance?.ipas || []
  const hasInPerson = (doctor.appointment_types || []).some(t => typeof t === 'string' && t.includes('in_person'))
  const hasVirtual = (doctor.appointment_types || []).some(t => typeof t === 'string' && t.includes('virtual'))

  // INS-1: Insurance match check (hook moved to top of component)
  const patientCarrierId = patientInsurance?.providerId || ''
  const patientCarrierName = (patientInsurance?.provider || '').toLowerCase()
  const patientIpaId = patientInsurance?.ipaId || ''
  const patientHasInsurance = !patientInsurance?.selfPay && (patientCarrierId || patientCarrierName)

  const insuranceMatch = isDoctorInNetwork(doctor, patientInsurance)

  // v214.9.3: In sheet mode, drop ResponsiveWrapper (the BottomSheet provides chrome)
  // and replace min-h-screen with a natural-height container that scrolls inside the sheet.
  // PROD-1431: the back / share row is pinned at the top. It used to sit INSIDE the hero, and a sticky
  // element can never leave its parent, so it is now the root's first child: exactly ROW_H tall, pulled
  // back over the hero by a negative margin, and the hero pays ROW_H as top padding, so nothing moves
  // at rest. The row paints the hero's own navy and bokeh (same box, same origin, clipped to the row),
  // so at rest it is invisible and once scrolled it is the navy bar plus the status-bar band.
  const ROW_H = asSheet ? '60px' : 'calc(60px + env(safe-area-inset-top, 0px))'
  const heroBand = (
    <div className="absolute top-0 left-0 right-0 overflow-hidden" style={{ background: '#15354F', height: asSheet ? '140px' : 'calc(140px + env(safe-area-inset-top, 0px))' }}>
      <div className="absolute rounded-full" style={{ top: -30, right: 30, width: 120, height: 120, background: '#3E6E97', opacity: 0.22 }} />
      <div className="absolute rounded-full" style={{ top: 38, right: -24, width: 70, height: 70, background: '#7FB4DC', opacity: 0.14 }} />
      <div className="absolute rounded-full" style={{ top: 10, left: 130, width: 28, height: 28, background: '#A7D9FC', opacity: 0.16 }} />
      <div className="absolute rounded-full" style={{ bottom: 14, right: 90, width: 16, height: 16, background: '#A7D9FC', opacity: 0.20 }} />
      <div className="absolute rounded-full" style={{ bottom: -12, left: 44, width: 46, height: 46, background: '#3E6E97', opacity: 0.16 }} />
    </div>
  )
  const inner = (
    <div className={`${asSheet ? 'bg-white flex flex-col overflow-y-auto flex-1 min-h-0' : 'screen-min-h bg-white flex flex-col'}`}>

      {/* Header buttons — PROD-1431: pinned at the top (sticky-inset-header); its navy top padding is the status-bar band */}
      <div className="sticky-inset-header flex-shrink-0 overflow-hidden px-4 pb-2 flex items-center justify-between" style={{ background: '#15354F', height: ROW_H, marginBottom: `calc(-1 * ${ROW_H})`, paddingTop: asSheet ? '16px' : 'calc(16px + env(safe-area-inset-top, 0px))' }}>
        {heroBand}
        <button onClick={goBack} className="relative w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
          <ArrowLeft size={20} className="text-white" />
        </button>
        <button onClick={handleShare} className="relative w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
          <Share2 size={18} className="text-white" />
        </button>
      </div>

      {/* ===== Blue accent hero ===== */}
      <div className="relative pb-6" style={{ paddingTop: ROW_H }}>
        {/* Navy hero bar — unified navy + bokeh (v242.18.0); fills the top safe-area in full-screen mode */}
        {heroBand}
        {/* Curved white separator */}
        <div className="absolute left-0 right-0 h-[60px] bg-white" style={{ top: asSheet ? '100px' : 'calc(100px + env(safe-area-inset-top, 0px))', borderRadius: '28px 28px 0 0' }} />

        {/* Floating profile card */}
        <div className="relative z-10 mx-4 mt-2 bg-white rounded-[20px] p-5" style={{ boxShadow: '0 8px 32px rgba(21,53,79,0.08)' }}>
          {/* Photo */}
          <div className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center border-[3px] border-orella-500 overflow-hidden" style={{ backgroundColor: '#E9F8FF' }}>
            {doctor.profile_photo ? (
              <img src={doctor.profile_photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-orella-500 font-bold text-2xl">{initials}</span>
            )}
          </div>

          {/* Name & specialty */}
          <h1 className="text-center text-lg font-bold" style={{ color: '#0F172A' }}>{fullName}</h1>
          <p className="text-center text-[13px] font-semibold text-orella-500 mt-1">{((doctor.specialties && doctor.specialties[0]) || '')}</p>

          {/* Credentials — colored text, no fill (v230.5.0) */}
          <div className="flex justify-center items-center gap-4 mt-2.5 flex-wrap">
            {doctor.board_certified && (
              <span className="text-[12px] font-bold flex items-center gap-1" style={{ color: '#15803D' }}>
                <ShieldCheck size={13} /> {t('share.boardCertified', 'Board certified')}
              </span>
            )}
          </div>

          {/* In-network / eligibility — plan-specific, colored text (v230.5.0) */}
          <CoverageStatus doctor={doctor} insurance={patientInsurance} navigate={navigate} />

          {/* Next available appointment — text + dot, no fill (v230.5.0) */}
          {nextSlot && (
            <div className="flex items-center justify-center gap-2 mt-3">
              <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ background: '#66D170' }} />
              <span className="text-[12.5px] font-bold" style={{ color: '#15354F' }}>
                {t('doctorProfile.nextAvailable', 'Next available:')} {(() => {
                  // v177.0.0: Handle grouped slot format {date, times: ['09:00', ...]}
                  const dateStr = nextSlot.date || nextSlot.start || ''
                  // Parse date parts to avoid UTC timezone shift (new Date('YYYY-MM-DD') is UTC midnight)
                  const [y, mo, dy] = (dateStr || '').split('-').map(Number)
                  const d = y ? new Date(y, mo - 1, dy) : new Date()
                  const today = new Date()
                  const tomorrow = new Date(today)
                  tomorrow.setDate(tomorrow.getDate() + 1)
                  const isToday = d.toDateString() === today.toDateString()
                  const isTomorrow = d.toDateString() === tomorrow.toDateString()
                  const dayLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                  // Use first time from times array (grouped format) or single time field
                  const rawTime = nextSlot.time || (Array.isArray(nextSlot.times) ? nextSlot.times[0] : null)
                  let timeStr = rawTime || ''
                  if (rawTime && /^\d{1,2}:\d{2}$/.test(rawTime)) {
                    const [h, m] = rawTime.split(':').map(Number)
                    const ampm = h >= 12 ? 'PM' : 'AM'
                    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
                    timeStr = `${h12}:${String(m).padStart(2, '0')} ${ampm}`
                  }
                  return `${dayLabel}${timeStr ? ` at ${timeStr}` : ''}`
                })()}
              </span>
            </div>
          )}

          {/* Stats row */}
          <div className="flex justify-around mt-4 pt-3.5 border-t border-gray-100">
            {doctor.google_rating && (
              <a
                href={doctor.google_place_id
                  ? `https://www.google.com/maps/place/?q=place_id:${doctor.google_place_id}`
                  : `https://www.google.com/search?q=Dr.+${doctor.first_name}+${doctor.last_name}+${((doctor.specialties && doctor.specialties[0]) || '') || ''}+reviews`}
                target="_blank" rel="noopener noreferrer"
                className="text-center active:opacity-70 transition-opacity"
              >
                <div className="flex items-center justify-center gap-1">
                  <Star size={14} className="fill-amber-400 text-amber-400" />
                  <p className="text-lg font-extrabold" style={{ color: '#0F172A' }}>{doctor.google_rating}</p>
                </div>
                <p className="text-[10px] font-medium" style={{ color: '#46A8F9', textDecoration: 'underline', textDecorationColor: '#A7D9FC' }}>
                  {doctor.google_review_count ? `${doctor.google_review_count} reviews` : 'Google'}
                </p>
              </a>
            )}
            {doctor.years_in_practice && (
              <div className="text-center">
                <p className="text-lg font-extrabold" style={{ color: '#0F172A' }}>{doctor.years_in_practice}+</p>
                <p className="text-[10px] font-medium" style={{ color: '#64748B' }}>{t('doctorProfile.yearsExp')}</p>
              </div>
            )}
            {(doctor.languages_spoken || []).length > 0 && (
              <div className="text-center">
                <p className="text-lg font-extrabold" style={{ color: '#0F172A' }}>{doctor.languages_spoken.length}</p>
                <p className="text-[10px] font-medium" style={{ color: '#64748B' }}>{doctor.languages_spoken.length === 1 ? 'Language' : 'Languages'}</p>
              </div>
            )}
            {(doctor.appointment_types || []).length > 0 && (
              <div className="text-center">
                <p className="text-lg font-extrabold" style={{ color: '#0F172A' }}>
                  {hasInPerson && hasVirtual ? '2' : '1'}
                </p>
                <p className="text-[10px] font-medium" style={{ color: '#64748B' }}>{t('doctorProfile.visitTypes')}</p>
              </div>
            )}
          </div>

          {/* Favorite pill + CTA (hidden in view mode) */}
          <div className="flex gap-2 mt-3.5">
            <button
              onClick={toggleFavorite}
              disabled={favLoading}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[13px] font-semibold transition-all active:scale-[0.97]"
              style={isFavorite ? {
                background: '#E9F8FF',
                color: '#1A6BB5',
                border: '1.5px solid #A7D9FC',
              } : {
                background: '#F8FAFC',
                color: '#64748B',
                border: '1.5px solid #E2E8F0',
              }}
            >
              <Heart size={15} className={isFavorite ? 'fill-[#46A8F9] text-[#46A8F9]' : 'text-[#94A3B8]'} />
              {isFavorite ? 'Saved' : 'Save'}
            </button>
            {mode !== 'view' && (
              <button
                onClick={handlePrimaryCTA}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[13px] font-semibold text-white active:scale-[0.97] transition-transform"
                style={{ background: '#15354F' }}
              >
                {getCTALabel()}
                {mode === 'select' && isSelected && selectedCtaProp && <ChevronRight size={15} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ===== Scrollable content ===== */}
      <div className={`${asSheet ? `px-5 ${mode === 'view' ? 'pb-6' : 'pb-4'}` : `flex-1 overflow-auto ${mode === 'view' ? 'pb-8' : ''} px-5`}`}>

        {/* About */}
        {doctor.bio && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: '#64748B' }}>{t('doctorProfile.about')}</p>
            <p className="text-[13px] leading-relaxed" style={{ color: '#1E293B' }}>{doctor.bio}</p>
          </div>
        )}

        {/* Education & Training */}
        {(doctor.education || []).length > 0 && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: '#64748B' }}>{t('doctorProfile.educationTraining')}</p>
            {doctor.education.map((edu, i) => (
              <div key={i} className="flex gap-3 mb-1.5 last:mb-0 p-2.5 rounded-xl" style={{ background: '#F8FAFC' }}>
                <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: '#E9F8FF' }}>
                  <GraduationCap size={17} className="text-orella-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold" style={{ color: '#0F172A' }}>{edu.institution}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>{edu.degree}{edu.field ? `, ${edu.field}` : ''}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Board Certifications */}
        {(doctor.board_certifications || []).length > 0 && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: '#64748B' }}>{t('doctorProfile.boardCertifications')}</p>
            {doctor.board_certifications.map((cert, i) => (
              <div key={i} className="flex gap-3 mb-1.5 last:mb-0 p-2.5 rounded-xl" style={{ background: '#F8FAFC' }}>
                <div className="w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: '#D5EFD3' }}>
                  <ShieldCheck size={17} className="text-green-700" />
                </div>
                <div className="flex items-center">
                  <p className="text-[13px] font-semibold" style={{ color: '#0F172A' }}>{cert}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Languages */}
        {(doctor.languages_spoken || []).length > 0 && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: '#64748B' }}>{t('doctorProfile.languagesSpoken')}</p>
            <div className="flex flex-wrap gap-1.5">
              {doctor.languages_spoken.map((lang, i) => (
                <span key={i} className="text-xs font-medium px-3.5 py-1.5 rounded-full" style={{ color: '#15354F', background: '#E9F8FF' }}>{lang}</span>
              ))}
            </div>
          </div>
        )}

        {/* Insurance Match Banner — only when specific carrier match, not when accept_all */}
        {insuranceMatch && patientHasInsurance && !acceptAll && (
          <div className="mb-3 flex items-center gap-2.5 p-3.5 rounded-xl" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(102,209,112,0.15)' }}>
              <ShieldCheck size={16} className="text-green-700" />
            </div>
            <div>
              <span className="text-[13px] font-semibold block" style={{ color: '#15803D' }}>{t('doctorProfile.acceptsYourInsurance')}</span>
              <span className="text-[11px]" style={{ color: '#166534' }}>{patientInsurance?.provider}</span>
            </div>
          </div>
        )}

        {/* Accepted Insurance */}
        {(insuranceItems.length > 0 || acceptAll) && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: '#64748B' }}>{t('doctorProfile.acceptedInsurance')}</p>
            {acceptAll ? (
              <div className="flex items-center gap-2.5 p-3.5 rounded-xl" style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(102,209,112,0.15)' }}>
                  <Shield size={15} className="text-green-700" />
                </div>
                <span className="text-[13px] font-semibold" style={{ color: '#15803D' }}>{t('doctorProfile.acceptsAllInsurancePlans')}</span>
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  {visibleInsurance.map((ins, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: '#EFF6FF' }}>
                      <span className="text-xs font-medium flex-shrink-0" style={{ color: '#1D4ED8' }}>{ins.name}</span>
                      {ins.acceptedPlanTypes.length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {ins.acceptedPlanTypes.map(pt => (
                            <span key={pt} className="text-[9px] px-1.5 py-0.5 rounded font-medium" style={{ background: '#DBEAFE', color: '#1E40AF' }}>{pt}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {!showAllInsurance && insuranceItems.length > 4 && (
                  <button 
                    onClick={() => setShowAllInsurance(true)}
                    className="text-xs px-3 py-1.5 rounded-full mt-2" style={{ color: '#64748B', background: '#F1F5F9' }}
                  >
                    +{insuranceItems.length - 4} more
                  </button>
                )}
                {showAllInsurance && insuranceItems.length > 4 && (
                  <button onClick={() => setShowAllInsurance(false)} className="text-xs text-orella-500 font-medium mt-2">{t('doctorProfile.showLess')}</button>
                )}
              </>
            )}

            {/* Contracted IPAs */}
            {acceptedIPAs.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: '#64748B' }}>{t('doctorProfile.contractedMedicalGroups')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {acceptedIPAs.map((ipa, i) => (
                    <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-full" style={{ color: '#0F6E56', background: 'rgba(102,209,112,0.1)' }}>
                      {typeof ipa === 'string' ? ipa : ipa.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Office Location */}
        {(doctor.practice_name || doctor.practice_address) && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: '#64748B' }}>{t('doctorProfile.officeLocation')}</p>
            <div className="rounded-[14px] p-4" style={{ border: '1px solid #E2E8F0' }}>
              <div className="flex gap-2.5 items-start">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#E9F8FF' }}>
                  <MapPin size={15} className="text-orella-500" />
                </div>
                <div>
                  {doctor.practice_name && <p className="text-[13px] font-semibold" style={{ color: '#0F172A' }}>{doctor.practice_name}</p>}
                  {/* v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string */}
                  {doctor.practice_address && <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>{formatAddress(doctor.practice_address)}</p>}
                  <p className="text-[11px]" style={{ color: '#64748B' }}>
                    {[doctor.practice_city, doctor.practice_state].filter(Boolean).join(', ')} {doctor.practice_zip}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <a href={getDirectionsUrl()} target="_blank" rel="noopener noreferrer"
                  className="flex-1 text-center py-2.5 rounded-[10px] text-xs font-semibold" style={{ background: '#E9F8FF', color: '#46A8F9' }}>
                  {t('visits.getDirections', 'Get directions')}
                </a>
                {doctor.practice_phone && (
                  <a href={`tel:${doctor.practice_phone}`}
                    className="flex-1 text-center py-2.5 rounded-[10px] text-xs font-semibold" style={{ background: '#E9F8FF', color: '#46A8F9' }}>
                    {t('doctorProfile.callOffice', 'Call office')}
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Visit Types */}
        {(doctor.appointment_types || []).length > 0 && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: '#64748B' }}>{t('doctorProfile.visitTypesAvailable')}</p>
            <div className="flex flex-wrap gap-2">
              {hasInPerson && (
                <span className="text-xs font-medium px-3.5 py-2 rounded-xl flex items-center gap-1.5" style={{ color: '#1D4ED8', background: '#EFF6FF' }}>
                  <MapPin size={13} className="text-orella-500" /> {t('doctorProfile.inPerson')}
                </span>
              )}
              {hasVirtual && (
                <span className="text-xs font-medium px-3.5 py-2 rounded-xl flex items-center gap-1.5" style={{ color: '#1D4ED8', background: '#EFF6FF' }}>
                  <Video size={13} className="text-orella-500" /> {t('caseHome.virtual', 'Virtual')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Contact */}
        {(doctor.practice_phone || doctor.practice_email || doctor.practice_website) && (
          <div className="mb-5">
            <p className="text-[10px] font-bold uppercase tracking-widest mb-2.5" style={{ color: '#64748B' }}>{t('doctorProfile.contact')}</p>
            <div className="space-y-1">
              {doctor.practice_phone && (
                <a href={`tel:${doctor.practice_phone}`} className="flex items-center gap-2.5 py-2.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F1F5F9' }}>
                    <Phone size={13} style={{ color: '#64748B' }} />
                  </div>
                  <span className="text-[13px] font-medium text-orella-500">{formatPhone(doctor.practice_phone)}</span>
                </a>
              )}
              {doctor.practice_email && (
                <a href={`mailto:${doctor.practice_email}`} className="flex items-center gap-2.5 py-2.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F1F5F9' }}>
                    <Mail size={13} style={{ color: '#64748B' }} />
                  </div>
                  <span className="text-[13px] font-medium text-orella-500">{doctor.practice_email}</span>
                </a>
              )}
              {doctor.practice_website && (
                <a href={doctor.practice_website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 py-2.5">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#F1F5F9' }}>
                    <Globe size={13} style={{ color: '#64748B' }} />
                  </div>
                  <span className="text-[13px] font-medium text-orella-500">{doctor.practice_website.replace(/^https?:\/\//, '')}</span>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Gender */}
        {doctor.gender && doctor.gender !== 'prefer-not-to-say' && (
          <p className="text-[11px] mt-2 mb-4" style={{ color: '#94A3B8' }}>
            {t('doctorProfile.genderLabel', 'Gender:')} {formatSex(doctor.gender)}
          </p>
        )}
      </div>

      {/* Bottom CTA — hidden in view mode. v237.14.1: also suppressed in sheet
          mode (share sequence). The floating profile card already carries the
          in-card "Select Doctor"/"Continue" CTA there; a second sticky bar just
          floats over the scroll content and obscures it. Standalone route mode
          carries the shared sticky bar (it takes its own room, so the content needs no reserved padding).
          PROD-1185: /doctor/:doctorId renders inside MobileLayout, whose TabBar is
          fixed at bottom-0, z-50, 4rem + max(inset - 14px, 0) tall. A bar at bottom-0
          sat under it and the booking button could not be seen. The bar now sits on
          top of the TabBar (the same clearance as ViewSummary's footer and
          MobileLayout's reserved paddingBottom, coupled to TabBar.jsx); the TabBar
          below it already pays the home-indicator inset, so the bar keeps plain p-4.
          Held by check-viewport-bottom-actions A7.
          PROD-1194 part C: the bar is now the shared <BottomActionBar aboveTabBar> — sticky in the one
          scroller with the SAME lift (index.css .bottom-action-bar[data-tabbar]), so it no longer floats
          over the last rows of the profile (a fixed bar took no room: the end of the page scrolled under
          it), and it gets the shared keyboard lift. Held by check-viewport-bottom-actions A12. */}
      {mode !== 'view' && !asSheet && (
        <BottomActionBar aboveTabBar className="[--bar-px:16px]">
          <button
            onClick={handlePrimaryCTA}
            className="w-full py-3.5 rounded-[14px] text-white font-bold text-[15px] active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
            style={{ background: '#15354F' }}
          >
            {getCTALabel()}
            {mode === 'select' && isSelected && selectedCtaProp && <ChevronRight size={18} />}
          </button>
        </BottomActionBar>
      )}
    </div>
  )

  return (
    <>
      {asSheet ? inner : <ResponsiveWrapper className="bg-white shadow-xl" safeArea={false}>{inner}</ResponsiveWrapper>}
      {showStartCase && <StartCaseSheet doctor={doctor} onClose={() => setShowStartCase(false)} />}
    </>
  )
}
